import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './ChartFullscreen.css';

interface Props {
  title?: string;
  subtitle?: string;
  legend?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}

export default function ChartFullscreen({
  title,
  subtitle,
  legend,
  onClose,
  children,
}: Props) {
  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC closes — standard modal affordance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="chart-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Expanded chart'}
    >
      <header className="chart-fullscreen__header">
        <div className="chart-fullscreen__heading">
          {title && <h2 className="chart-fullscreen__title">{title}</h2>}
          {subtitle && <p className="chart-fullscreen__subtitle">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="chart-fullscreen__close"
          onClick={onClose}
          aria-label="Close fullscreen chart"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>
      <div className="chart-fullscreen__body">
        {legend && <div className="chart-fullscreen__legend">{legend}</div>}
        <div className="chart-fullscreen__chart">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
